import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SimpleAudioButton from './audio/SimpleAudioButton';

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  
  // Function to scroll to CTA section or navigate
  const scrollToCTA = () => {
    // Option 1: Scroll to the CTA section if it's on the same page
    const ctaSection = document.getElementById('cta-section');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Option 2: Navigate to home page with CTA section hash
      navigate('/#cta-section');
    }
  };

  // Special pricing (33% off regular prices)
  const monthlyPrice = {
    basic: 9.99,  // Was 14.99
    premium: 19.99 // Was 29.99 - "premium" key kept for code compatibility, represents Voice Connect
  };

  const annualPrice = {
    basic: 7.99,  // Was 11.99 (95.88/year ÷ 12 = ~7.99/month)
    premium: 15.99 // Was 23.99 (191.88/year ÷ 12 = ~15.99/month)
  };

  const currentPrices = isAnnual ? annualPrice : monthlyPrice;
  const billingPeriod = isAnnual ? 'monthly' : 'monthly'; // Both display as monthly price
  
  // Calculate annual totals for display
  const annualTotal = {
    basic: (annualPrice.basic * 12).toFixed(2),
    premium: (annualPrice.premium * 12).toFixed(2)
  };
  
  // Calculate savings
  const savedAmount = {
    basic: ((monthlyPrice.basic * 12) - (annualPrice.basic * 12)).toFixed(2),
    premium: ((monthlyPrice.premium * 12) - (annualPrice.premium * 12)).toFixed(2)
  };

  return (
    <section id="pricing" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block bg-[#F4B6A1]/20 px-4 py-2 rounded-full mb-4">
            <span className="text-[#F4B6A1] font-medium">Simple Pricing</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 font-['Poppins']">
            Choose Your <span className="text-[#C2A4D6]">Support Plan</span>
          </h2>
          <div className="flex justify-center mb-6">
            <SimpleAudioButton audioSrc="/assets/audio/text-vs-voice.mp3" />
          </div>
          <p className="text-lg text-gray-600 font-['Merriweather'] mb-8">
            Select the plan that fits your needs. Start with our 7-day free trial 
            to experience the difference before committing.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-8">
            <span className={`mr-4 ${!isAnnual ? 'font-semibold text-[#C2A4D6]' : 'text-gray-600'}`}>
              Monthly
            </span>
            <button 
              className={`relative w-16 h-8 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C2A4D6] ${
                isAnnual ? 'bg-[#C2A4D6]' : 'bg-gray-300'
              }`}
              onClick={() => setIsAnnual(!isAnnual)}
            >
              <span 
                className={`absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform transform ${
                  isAnnual ? 'translate-x-8' : ''
                }`}
              />
            </button>
            <span className={`ml-4 ${isAnnual ? 'font-semibold text-[#C2A4D6]' : 'text-gray-600'}`}>
              Annual <span className="text-[#F4B6A1] text-sm font-medium">(Save 20%)</span>
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Text Companion Plan */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-transform transform hover:scale-105">
            <div className="bg-[#B3D9E6]/20 p-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-1 font-['Poppins']">Essential Support</h3>
              <p className="text-gray-600 mb-4">Perfect for occasional check-ins</p>
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-gray-900">${currentPrices.basic}</span>
                <span className="text-gray-600 ml-1">/{billingPeriod}</span>
              </div>
              {isAnnual && (
                <div>
                  <p className="text-gray-600 text-sm mt-1">
                    ${annualTotal.basic} billed annually
                  </p>
                  <p className="text-[#F4B6A1] text-sm mt-1">
                    Save ${savedAmount.basic} per year
                  </p>
                </div>
              )}
            </div>
            
            <div className="p-6 space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Essential emotional support conversations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Text-based conversations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Basic emotional awareness building</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Adapative conversational intelligence</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">24/7 availability</span>
                </li>
              </ul>

              <div className="pt-4">
                <button 
                  onClick={scrollToCTA}
                  className="w-full bg-white hover:bg-gray-50 text-[#C2A4D6] border-2 border-[#C2A4D6] py-3 px-6 rounded-full text-lg font-medium transition-colors"
                >
                  Start 7-Day Free Trial
                </button>
              </div>
            </div>
          </div>

          {/* Voice Connect Plan */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-transform transform hover:scale-105 relative">
            <div className="absolute top-0 right-0">
              <div className="bg-[#F4B6A1] text-white px-4 py-1 rounded-bl-lg font-medium text-sm">
                Most Popular
              </div>
            </div>
            
            <div className="bg-[#C2A4D6]/20 p-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-1 font-['Poppins']">Voice Connect</h3>
              <p className="text-gray-600 mb-4">For comprehensive emotional support</p>
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-gray-900">${currentPrices.premium}</span>
                <span className="text-gray-600 ml-1">/{billingPeriod}</span>
              </div>
              {isAnnual && (
                <div>
                  <p className="text-gray-600 text-sm mt-1">
                    ${annualTotal.premium} billed annually
                  </p>
                  <p className="text-[#F4B6A1] text-sm mt-1">
                    Save ${savedAmount.premium} per year
                  </p>
                </div>
              )}
            </div>
            
            <div className="p-6 space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Comprehensive emotional wellness support</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Text AND voice conversations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Advanced emotional awareness building</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Personalized growth insights</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Voice Connect and pattern analysis</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Extended conversation history</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Tailored emotional wellness tips</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">FeelHeard adaptive conversational intelligence</span>
                </li>
              </ul>

              <div className="pt-4">
                <button 
                  onClick={scrollToCTA}
                  className="w-full bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-3 px-6 rounded-full text-lg font-medium transition-colors"
                >
                  Start 7-Day Free Trial
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center max-w-3xl mx-auto">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Our Satisfaction Guarantee</h3>
            <p className="text-gray-600">
              If you don't feel genuinely supported after your first 5 conversations,
              we'll refund your subscription. No questions asked. To cancel, use the link in your
              subscription confirmation email or email support@feelheard.me.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;